import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiRotateCcw, FiHome, FiUsers, FiCpu, FiList } = FiIcons;

const GameInfo = ({ 
  mode, 
  currentPlayer, 
  gameStatus, 
  moveHistory, 
  capturedPieces, 
  isComputerThinking,
  onReset, 
  onGoToMenu 
}) => {
  const getPieceSymbol = (piece) => {
    const symbols = {
      white: {
        king: '♔', queen: '♕', rook: '♖', 
        bishop: '♗', knight: '♘', pawn: '♙'
      },
      black: {
        king: '♚', queen: '♛', rook: '♜', 
        bishop: '♝', knight: '♞', pawn: '♟'
      }
    };
    return symbols[piece.color][piece.type] || '';
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      {/* Game Mode */}
      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
        <div className="flex items-center gap-3 mb-4">
          <SafeIcon 
            icon={mode === 'two-player' ? FiUsers : FiCpu} 
            className="text-2xl text-blue-400" 
          />
          <h2 className="text-xl font-bold text-white">
            {mode === 'two-player' ? 'Two Players' : 'vs Computer'}
          </h2>
        </div>
        
        <div className="flex gap-3">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onReset}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
          >
            <SafeIcon icon={FiRotateCcw} />
            Reset
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onGoToMenu}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors"
          >
            <SafeIcon icon={FiHome} />
            Menu
          </motion.button>
        </div>
      </div>

      {/* Captured Pieces */}
      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
        <h3 className="text-lg font-bold text-white mb-4">Captured Pieces</h3>
        
        <div className="space-y-3">
          <div>
            <p className="text-gray-300 text-sm mb-2">White captured:</p>
            <div className="flex flex-wrap gap-1">
              {capturedPieces.white.map((piece, index) => (
                <span key={index} className="text-xl">
                  {getPieceSymbol(piece)}
                </span>
              ))}
              {capturedPieces.white.length === 0 && (
                <span className="text-gray-500 text-sm">None</span>
              )}
            </div>
          </div>
          
          <div>
            <p className="text-gray-300 text-sm mb-2">Black captured:</p>
            <div className="flex flex-wrap gap-1">
              {capturedPieces.black.map((piece, index) => (
                <span key={index} className="text-xl">
                  {getPieceSymbol(piece)}
                </span>
              ))}
              {capturedPieces.black.length === 0 && (
                <span className="text-gray-500 text-sm">None</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Move History */}
      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
        <div className="flex items-center gap-2 mb-4">
          <SafeIcon icon={FiList} className="text-white" />
          <h3 className="text-lg font-bold text-white">Move History</h3>
        </div>
        
        <div className="max-h-48 overflow-y-auto space-y-1">
          {moveHistory.length === 0 ? (
            <p className="text-gray-500 text-sm">No moves yet</p>
          ) : (
            moveHistory.slice(-10).map((move, index) => {
              const moveNumber = moveHistory.length - 10 + index + 1;
              const fromSquare = String.fromCharCode(97 + move.from[1]) + (8 - move.from[0]);
              const toSquare = String.fromCharCode(97 + move.to[1]) + (8 - move.to[0]);
              
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center gap-2 text-sm text-gray-300 py-1"
                >
                  <span className="text-gray-500 w-8">{moveNumber}.</span>
                  <span className="text-xl">{getPieceSymbol(move.piece)}</span>
                  <span>{fromSquare} → {toSquare}</span>
                  {move.captured && (
                    <span className="text-red-400">
                      ×{getPieceSymbol(move.captured)}
                    </span>
                  )}
                </motion.div>
              );
            })
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default GameInfo;